import React, { useEffect, useMemo, useRef, useState } from 'react';
import { trackingLinkUrl, formatVisitDate, deleteTrackingLink, updateTrackingLink, descriptionBullets } from '../../utils/trackingLinks';
import IncludedItemsModal from './IncludedItemsModal';
import DescriptionItemsInput from './DescriptionItemsInput';
import { MONO, SANS } from '../../constants/fonts';

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '80vh',
    background: '#fff',
    border: '1px solid #e5e5ea',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    fontFamily: SANS,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 12px',
  },
  title: {
    fontFamily: MONO,
    fontSize: '1.05rem',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: MONO,
    fontSize: '0.95rem',
    color: '#222',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
  },
  close: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    lineHeight: 1,
    color: '#999',
    cursor: 'pointer',
  },
  search: {
    margin: '0 20px 12px',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
    padding: '0 20px 20px',
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    border: '1px solid #eee',
    borderRadius: 8,
    fontSize: '0.85rem',
    marginBottom: 8,
    cursor: 'pointer',
    background: '#fff',
  },
  name: {
    fontWeight: 600,
    color: '#222',
  },
  meta: {
    color: '#888',
    fontSize: '0.78rem',
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
  },
  empty: {
    color: '#999',
    fontSize: '0.85rem',
    padding: '0 20px 20px',
  },
  detail: {
    padding: '0 20px 20px',
    overflowY: 'auto',
  },
  detailName: {
    fontFamily: MONO,
    fontSize: '1.1rem',
    marginBottom: 16,
  },
  linkBox: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    padding: 10,
    background: '#f6f6f4',
    borderRadius: 8,
  },
  linkText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  copyButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  fieldLabel: {
    color: '#888',
  },
  descriptionBlock: {
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  previewBulletList: {
    listStyle: 'none',
    margin: '6px 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  previewBulletItem: {
    fontSize: '0.85rem',
    color: '#333',
  },
  deleteButton: {
    marginTop: 20,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #f0c4c0',
    background: '#fff',
    color: '#c0392b',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  actionsRow: {
    display: 'flex',
    gap: 8,
    marginTop: 20,
  },
  editButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#222',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  categoryRow: {
    display: 'flex',
    gap: 8,
  },
  categoryButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#555',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  categoryButtonActive: {
    background: '#111',
    color: '#fff',
    border: '1px solid #111',
  },
  formLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    margin: '16px 0 6px',
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  formSelect: {
    width: '100%',
    padding: '10px 36px 10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    background: '#fff',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    backgroundSize: '11px 7px',
  },
  formTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 60,
    fontFamily: 'inherit',
  },
  priceInputWrap: {
    position: 'relative',
  },
  priceSymbol: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#888',
    fontSize: '0.9rem',
    pointerEvents: 'none',
  },
  priceInput: {
    width: '100%',
    padding: '10px 12px 10px 26px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
  },
  saveRow: {
    display: 'flex',
    gap: 8,
    marginTop: 20,
  },
  saveButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  formError: {
    color: '#c0392b',
    fontSize: '0.8rem',
    marginTop: 10,
  },
  confirmBox: {
    marginTop: 20,
    padding: 12,
    borderRadius: 8,
    border: '1px solid #f0c4c0',
    background: '#fdf3f2',
  },
  confirmText: {
    fontSize: '0.85rem',
    color: '#8a2c22',
    marginBottom: 10,
  },
  confirmRow: {
    display: 'flex',
    gap: 8,
  },
  confirmDeleteButton: {
    padding: '8px 14px',
    borderRadius: 8,
    border: 'none',
    background: '#c0392b',
    color: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#222',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  presetsButton: {
    width: '100%',
    marginTop: 8,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px dashed #c5c5cc',
    background: '#fafafa',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      style={styles.copyButton}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function DeleteLinkButton({ link }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!confirming) {
    return (
      <button type="button" style={styles.deleteButton} onClick={() => setConfirming(true)}>
        Delete link
      </button>
    );
  }

  return (
    <div style={styles.confirmBox}>
      <div style={styles.confirmText}>Delete the link for "{link.name}"? This can't be undone.</div>
      <div style={styles.confirmRow}>
        <button
          type="button"
          style={styles.confirmDeleteButton}
          disabled={deleting}
          onClick={async () => {
            setDeleting(true);
            await deleteTrackingLink(link.id);
          }}
        >
          {deleting ? 'Deleting…' : 'Yes, delete'}
        </button>
        <button type="button" style={styles.cancelButton} onClick={() => setConfirming(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function LinkEditForm({ link, onCancel, onSaved }) {
  const [name, setName] = useState(link.name);
  const [category, setCategory] = useState(link.category || 'website');
  const [offerType, setOfferType] = useState(link.offerType || 'none');
  const [discountPercent, setDiscountPercent] = useState(link.discountPercent ?? '');
  const [customPrice, setCustomPrice] = useState((link.customPrice || '').replace(/^£/, ''));
  const [customDescription, setCustomDescription] = useState(link.customDescription || '');
  const [essentialMode, setEssentialMode] = useState(link.essentialPlanMode || 'none');
  const [essentialPercent, setEssentialPercent] = useState(link.essentialPlanPercent ?? '');
  const [essentialCustom, setEssentialCustom] = useState(link.essentialPlanCustom ?? '');
  const [activeMode, setActiveMode] = useState(link.activePlanMode || 'none');
  const [activePercent, setActivePercent] = useState(link.activePlanPercent ?? '');
  const [activeCustom, setActiveCustom] = useState(link.activePlanCustom ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [includedItemsModalOpen, setIncludedItemsModalOpen] = useState(false);
  const [itemsSaveState, setItemsSaveState] = useState(null); // null | 'saving' | 'saved' | 'error'
  const savedTimeout = useRef(null);

  useEffect(() => () => clearTimeout(savedTimeout.current), []);

  const buildOffer = (descriptionValue = customDescription) => offerType === 'none' ? null : {
    type: offerType,
    discountPercent: offerType === 'discount' ? Number(discountPercent) : undefined,
    customPrice: offerType === 'custom' ? `£${customPrice.trim()}` : undefined,
    customDescription: (offerType === 'custom' || offerType === 'free') ? descriptionValue.trim() : undefined,
  };

  const buildCarePlan = () => (essentialMode === 'none' && activeMode === 'none') ? null : {
    essential: essentialMode === 'none' ? undefined : {
      mode: essentialMode,
      percent: essentialMode === 'percent' ? Number(essentialPercent) : undefined,
      custom: essentialMode === 'custom' ? Number(essentialCustom) : undefined,
    },
    active: activeMode === 'none' ? undefined : {
      mode: activeMode,
      percent: activeMode === 'percent' ? Number(activePercent) : undefined,
      custom: activeMode === 'custom' ? Number(activeCustom) : undefined,
    },
  };

  // Included items save straight to Firestore as they're added/removed —
  // there's already a real record to update here (unlike the create form,
  // where the link doesn't exist yet until the whole form is submitted).
  const persistDescription = async (newDescription) => {
    clearTimeout(savedTimeout.current);
    setItemsSaveState('saving');
    try {
      await updateTrackingLink(link.id, name, buildOffer(newDescription), category, buildCarePlan());
      setItemsSaveState('saved');
      savedTimeout.current = setTimeout(() => setItemsSaveState(null), 1500);
    } catch (err) {
      setItemsSaveState('error');
    }
  };

  const handleDescriptionChange = (newDescription) => {
    setCustomDescription(newDescription);
    persistDescription(newDescription);
  };

  const applyPresets = (items) => {
    const trimmed = customDescription.trim();
    const next = trimmed ? `${trimmed}\n${items.join('\n')}` : items.join('\n');
    setCustomDescription(next);
    persistDescription(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await updateTrackingLink(link.id, name, buildOffer(), category, buildCarePlan());
      onSaved();
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form style={styles.detail} onSubmit={handleSubmit}>
      <div style={styles.formLabel}>Name</div>
      <input
        style={styles.formInput}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />

      <div style={styles.formLabel}>Category</div>
      <div style={styles.categoryRow}>
        <button
          type="button"
          style={{ ...styles.categoryButton, ...(category === 'website' ? styles.categoryButtonActive : {}) }}
          onClick={() => setCategory('website')}
        >
          Website
        </button>
        <button
          type="button"
          style={{ ...styles.categoryButton, ...(category === 'app' ? styles.categoryButtonActive : {}) }}
          onClick={() => setCategory('app')}
        >
          App
        </button>
      </div>

      <div style={styles.formLabel}>Offer</div>
      <select
        style={styles.formSelect}
        value={offerType}
        onChange={(e) => setOfferType(e.target.value)}
      >
        <option value="none">No special offer</option>
        <option value="discount">Discount %</option>
        <option value="free">Free</option>
        <option value="custom">Custom price</option>
      </select>

      {offerType === 'discount' && (
        <>
          <div style={styles.formLabel}>Discount percentage</div>
          <input
            style={styles.formInput}
            type="number"
            min="1"
            max="100"
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            required
          />
        </>
      )}

      {(offerType === 'custom' || offerType === 'free') && (
        <>
          {offerType === 'custom' && (
            <>
              <div style={styles.formLabel}>Price</div>
              <div style={styles.priceInputWrap}>
                <span style={styles.priceSymbol}>£</span>
                <input
                  style={styles.priceInput}
                  type="text"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          <div style={styles.formLabel}>Description</div>
          <DescriptionItemsInput value={customDescription} onChange={handleDescriptionChange} saveState={itemsSaveState} />
          <button type="button" style={styles.presetsButton} onClick={() => setIncludedItemsModalOpen(true)}>
            + Add included items
          </button>
        </>
      )}

      <div style={styles.formLabel}>Essential plan discount</div>
      <select
        style={styles.formSelect}
        value={essentialMode}
        onChange={(e) => setEssentialMode(e.target.value)}
      >
        <option value="none">No change</option>
        <option value="percent">% off</option>
        <option value="free">Free</option>
        <option value="custom">Custom price</option>
      </select>

      {essentialMode === 'percent' && (
        <input
          style={styles.formInput}
          type="number"
          min="1"
          max="100"
          value={essentialPercent}
          onChange={(e) => setEssentialPercent(e.target.value)}
          required
        />
      )}

      {essentialMode === 'custom' && (
        <div style={styles.priceInputWrap}>
          <span style={styles.priceSymbol}>£</span>
          <input
            style={styles.priceInput}
            type="text"
            value={essentialCustom}
            onChange={(e) => setEssentialCustom(e.target.value)}
            required
          />
        </div>
      )}

      <div style={styles.formLabel}>Active plan discount</div>
      <select
        style={styles.formSelect}
        value={activeMode}
        onChange={(e) => setActiveMode(e.target.value)}
      >
        <option value="none">No change</option>
        <option value="percent">% off</option>
        <option value="free">Free</option>
        <option value="custom">Custom price</option>
      </select>

      {activeMode === 'percent' && (
        <input
          style={styles.formInput}
          type="number"
          min="1"
          max="100"
          value={activePercent}
          onChange={(e) => setActivePercent(e.target.value)}
          required
        />
      )}

      {activeMode === 'custom' && (
        <div style={styles.priceInputWrap}>
          <span style={styles.priceSymbol}>£</span>
          <input
            style={styles.priceInput}
            type="text"
            value={activeCustom}
            onChange={(e) => setActiveCustom(e.target.value)}
            required
          />
        </div>
      )}

      {error && <div style={styles.formError}>{error}</div>}

      <div style={styles.saveRow}>
        <button type="submit" style={styles.saveButton} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" style={styles.cancelButton} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>

    {includedItemsModalOpen && (
      <IncludedItemsModal
        existingDescription={customDescription}
        onApply={applyPresets}
        onClose={() => setIncludedItemsModalOpen(false)}
      />
    )}
    </>
  );
}

function LinkDetail({ link }) {
  const [editing, setEditing] = useState(false);
  const url = trackingLinkUrl(link.id);

  if (editing) {
    return <LinkEditForm link={link} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} />;
  }

  return (
    <div style={styles.detail}>
      <div style={styles.detailName}>{link.name}</div>
      <div style={styles.linkBox}>
        <div style={styles.linkText}>{url}</div>
        <CopyButton text={url} />
      </div>
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Category</span>
        <span>{link.category === 'app' ? 'App' : 'Website'}</span>
      </div>
      {link.offerType === 'discount' && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Offer</span>
          <span>{link.discountPercent}% discount</span>
        </div>
      )}
      {link.offerType === 'free' && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Offer</span>
          <span>Free build</span>
        </div>
      )}
      {link.offerType === 'custom' && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Offer</span>
          <span>{link.customPrice}</span>
        </div>
      )}
      {(link.offerType === 'custom' || link.offerType === 'free') && descriptionBullets(link.customDescription).length > 0 && (
        <div style={styles.descriptionBlock}>
          <div style={styles.fieldLabel}>Description</div>
          <ul style={styles.previewBulletList}>
            {descriptionBullets(link.customDescription).map((line, i) => (
              <li key={i} style={styles.previewBulletItem}>• {line}</li>
            ))}
          </ul>
        </div>
      )}
      {link.essentialPlanMode && link.essentialPlanMode !== 'none' && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Essential plan</span>
          <span>
            {link.essentialPlanMode === 'percent' && `${link.essentialPlanPercent}% off`}
            {link.essentialPlanMode === 'free' && 'Free'}
            {link.essentialPlanMode === 'custom' && `£${link.essentialPlanCustom}`}
          </span>
        </div>
      )}
      {link.activePlanMode && link.activePlanMode !== 'none' && (
        <div style={styles.fieldRow}>
          <span style={styles.fieldLabel}>Active plan</span>
          <span>
            {link.activePlanMode === 'percent' && `${link.activePlanPercent}% off`}
            {link.activePlanMode === 'free' && 'Free'}
            {link.activePlanMode === 'custom' && `£${link.activePlanCustom}`}
          </span>
        </div>
      )}
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Visited</span>
        <span>{link.visited ? 'Yes' : 'No'}</span>
      </div>
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Visit count</span>
        <span>{link.visitCount}</span>
      </div>
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Last visited</span>
        <span>{formatVisitDate(link.lastVisitedAt) || '—'}</span>
      </div>
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Created</span>
        <span>{formatVisitDate(link.createdAt) || '—'}</span>
      </div>
      <div style={styles.fieldRow}>
        <span style={styles.fieldLabel}>Created by</span>
        <span>{link.createdBy}</span>
      </div>
      <div style={styles.actionsRow}>
        <button type="button" style={styles.editButton} onClick={() => setEditing(true)}>
          Edit
        </button>
      </div>
      <DeleteLinkButton link={link} />
    </div>
  );
}

export default function TrackingLinksModal({ links, loading, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return links;
    return links.filter((link) => link.name.toLowerCase().includes(q));
  }, [links, query]);

  const selected = links.find((link) => link.id === selectedId);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          {selected ? (
            <button type="button" style={styles.backButton} onClick={() => setSelectedId(null)}>
              ← Tracking links
            </button>
          ) : (
            <div style={styles.title}>Tracking links</div>
          )}
          <button type="button" style={styles.close} aria-label="Close" onClick={onClose}>
            &times;
          </button>
        </div>

        {selected ? (
          <LinkDetail link={selected} />
        ) : (
          <>
            <input
              style={styles.search}
              type="text"
              placeholder="Search by name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <div style={styles.list}>
              {loading && <div style={styles.empty}>Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div style={styles.empty}>{links.length === 0 ? 'No links yet.' : 'No matches.'}</div>
              )}
              {filtered.map((link) => (
                <div key={link.id} style={styles.listRow} onClick={() => setSelectedId(link.id)}>
                  <div>
                    <span style={{ ...styles.dot, background: link.visited ? '#1a7f37' : '#ccc' }} />
                    <span style={styles.name}>{link.name}</span>
                  </div>
                  <div style={styles.meta}>
                    {link.category === 'app' ? 'App' : 'Website'} · {link.visited ? `${link.visitCount}x` : 'Not visited'}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
